<template>
  <canvas
    ref="canvas"
    class="canvas-text"
    :style="{ width: cssWidth + 'px', height: cssHeight + 'px' }"
  ></canvas>
</template>

<script>
export default {
  name: "CanvasText",
  props: {
    // 要画的文字
    text: { type: [String, Number], required: true },
    // 字号。unit=px 时为像素；unit=rem 时为 rem，会按根字号实时换算
    fontSize: { type: Number, default: 16 },
    unit: { type: String, default: "px" }, // 'px' | 'rem'
    fontFamily: { type: String, default: '"PingFang SC", sans-serif' },
    fontWeight: { type: [String, Number], default: "normal" },
    color: { type: String, default: "#333333" },
    // 最大宽度(同 unit)，超出自动换行；不传则单行
    maxWidth: { type: Number, default: 0 },
    lineHeight: { type: Number, default: 1.4 },
    align: { type: String, default: "left" }, // left / center / right
  },
  data() {
    return {
      cssWidth: 0,
      cssHeight: 0,
    };
  },
  watch: {
    text: "redraw",
    fontSize: "redraw",
    color: "redraw",
    fontWeight: "redraw",
    maxWidth: "redraw",
  },
  mounted() {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.redraw);
    } else {
      this.redraw();
    }
    window.addEventListener("resize", this.redraw);
    window.addEventListener("orientationchange", this.redraw);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.redraw);
    window.removeEventListener("orientationchange", this.redraw);
  },
  methods: {
    rootFontSize() {
      return (
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      );
    },
    toPx(v) {
      return this.unit === "rem" ? v * this.rootFontSize() : v;
    },
    splitLines(ctx, text, maxWidth) {
      if (!maxWidth) return String(text).split("\n");
      const lines = [];
      const paragraphs = String(text).split("\n");
      for (let p = 0; p < paragraphs.length; p++) {
        let line = "";
        const chars = Array.from(paragraphs[p]);
        for (let i = 0; i < chars.length; i++) {
          const test = line + chars[i];
          if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = chars[i];
          } else {
            line = test;
          }
        }
        lines.push(line);
      }
      return lines;
    },
    draw() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;

      const fontPx = this.toPx(this.fontSize);
      const maxWidthPx = this.maxWidth ? this.toPx(this.maxWidth) : 0;
      const font = `${this.fontWeight} ${fontPx}px ${this.fontFamily}`;

      ctx.font = font;
      const lines = this.splitLines(ctx, this.text, maxWidthPx);

      const lineH = fontPx * this.lineHeight;
      let width = maxWidthPx;
      if (!width) {
        width = Math.ceil(
          Math.max.apply(
            null,
            lines.map((l) => ctx.measureText(l).width)
          )
        );
      }
      const height = Math.ceil(lineH * lines.length);

      this.cssWidth = width;
      this.cssHeight = height;

      // 高清屏：物理像素放大，再缩放绘图坐标
      canvas.width = Math.ceil(width * dpr);
      canvas.height = Math.ceil(height * dpr);
      ctx.scale(dpr, dpr);

      ctx.font = font;
      ctx.fillStyle = this.color;
      ctx.textBaseline = "middle";
      ctx.textAlign = this.align;

      let x = 0;
      if (this.align === "center") x = width / 2;
      else if (this.align === "right") x = width;

      lines.forEach((line, i) => {
        ctx.fillText(line, x, lineH * i + lineH / 2);
      });
    },
    redraw() {
      this.$nextTick(this.draw);
    },
  },
};
</script>

<style scoped>
.canvas-text {
  display: inline-block;
  vertical-align: middle;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}
</style>
